import { apply, burst, celebrate, onceForever, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say } from '../ui/dialog';
import { stamp } from './passport';
import { registerObjects, registerTalk } from './registry';
import { hualienShuttle, order, quiz } from './trips';

/** Interactions of Taroko Gorge and Qixingtan (map hualien.ts, taroko package). */

const tk = () => m().taroko;
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const shuffled = <T>(a: readonly T[]): T[] => [...a].sort(() => Math.random() - 0.5);

/** A fact with one action button; `onAct` runs the reward and returns the follow-up line. */
function landmark(id: string, icon: string, text: string, act: string, onAct: () => string, en?: string): void {
  say({
    who: objName(id),
    icon,
    text,
    en,
    choices: [{ text: act, run: () => say({ who: objName(id), icon, text: onAct(), en }) }, { text: m().common.back }],
  });
}

/** A fact that leads into a `quiz`; `reward` runs once per day on a right answer. */
function factQuiz(id: string, icon: string, text: string, act: string, t: { q: string; a: readonly string[]; right: string; wrong: string }, reward: () => void): void {
  say({
    who: objName(id),
    icon,
    text,
    choices: [{ text: act, run: () => quiz(objName(id), icon, text, t.q, t.a, t.right, t.wrong, () => onceToday(`tk.${id}`) && reward()) }, { text: m().common.back }],
  });
}

function swallows(): void {
  const t = tk().swallows;
  const n = 3 + Math.floor(Math.random() * 6);
  const words = t.numbers;
  const right = words[n];
  const wrong = shuffled([words[n - 1], words[n + 1], words[n + 2]]).slice(0, 2);
  say({
    who: objName('tk_swallows'),
    icon: '🐦',
    text: t.text,
    choices: [
      {
        text: t.count,
        run: () => {
          sfx('flap');
          quiz(objName('tk_swallows'), '🐦', t.text, t.q('🐦 '.repeat(n).trim()), [right, ...wrong], t.right, t.wrong, () => {
            if (onceToday('tk.swallows')) apply({ study: 6, stars: 2 });
            stamp('taroko');
          });
        },
      },
      { text: m().common.back },
    ],
  });
}

function skipStone(): void {
  const t = tk().skip;
  const throwIt = () => {
    sfx('drop');
    const r = Math.random();
    const n = r < 0.12 ? 0 : Math.min(9, 1 + Math.floor(r * r * 9));
    const counted = tk().swallows.numbers.slice(1, n + 1).join(', ');
    if (n) sayEnglish(counted, 1);
    if (n >= 5) {
      burst('sparkle', 8);
      if (onceToday('tk.skip')) apply({ mood: 12, stars: 3 });
      stamp('beach');
    } else apply({ minutes: 2 }, { float: false });
    say({ who: objName('tk_skip'), icon: '🥏', text: `${t.result(n, n ? `(${counted}!)` : '')}${n >= 5 ? `\n\n${t.great}` : ''}`, choices: [{ text: t.again, run: throwIt }, { text: m().common.back }] });
  };
  say({ who: objName('tk_skip'), icon: '🥏', text: t.text, choices: [{ text: t.throw, run: throwIt }, { text: m().common.back }] });
}

function sunrise(): void {
  const t = tk().sunrise;
  const time = S().time;
  if (time >= 5 * 60 && time < 7 * 60) {
    landmark('tk_sunrise', '🌅', t.text, t.early, () => {
      sfx('fanfare');
      celebrate();
      sayEnglish(t.phrase, 0.85);
      if (onceForever('tk.sunrisePhoto')) apply({ mood: 20, stars: 8 });
      else if (onceToday('tk.sunrise')) apply({ mood: 12, stars: 2 });
      stamp('beach');
      return `${t.photo}\n\n“${t.phrase}”`;
    }, t.phrase);
  } else {
    sayEnglish(t.phrase, 0.85);
    say({ who: objName('tk_sunrise'), icon: '🌅', text: `${t.text}\n\n${t.late}\n\n“${t.phrase}”`, en: t.phrase });
  }
}

function stargaze(): void {
  const t = tk().stars;
  const time = S().time;
  const night = time >= 19 * 60 || time < 5 * 60;
  if (!night) return say({ who: objName('tk_stargaze'), icon: '✨', text: `${t.text}\n\n${t.day}` });
  factQuiz('tk_stargaze', '✨', t.text, t.look, t, () => {
    apply({ study: 6, stars: 4, mood: 10 });
    stamp('beach');
  });
}

function bikeRide(): void {
  const t = tk().bike;
  say({
    who: objName('tk_bikeRent'),
    icon: '🚲',
    text: t.text,
    choices: [
      {
        text: t.ride,
        cost: m().world3.market.cost(t.price),
        run: () => {
          if (S().coins < t.price) return toast(t.poor);
          quiz(objName('tk_bikeRent'), '🚲', t.text, t.q, t.a, t.right, t.wrong, () => {
            apply({ coins: -t.price, mood: 15, energy: -5, minutes: 40 });
            if (onceToday('tk.bike')) apply({ stars: 2 }, { float: false });
            stamp('beach');
          });
        },
      },
      { text: m().common.back },
    ],
  });
}

registerObjects({
  tk_shuttleTaroko: () => hualienShuttle('taroko'),
  tk_shuttleBeach: () => hualienShuttle('qixingtan'),
  tk_visitor() {
    const t = tk().visitor;
    factQuiz('tk_visitor', 'ℹ️', t.text, t.ask, t, () => apply({ study: 5, stars: 1 }));
  },
  tk_cimu() {
    const t = tk().cimu;
    landmark('tk_cimu', '🌉', t.text, t.listen, () => {
      sfx('water');
      sayEnglish(t.phrase, 0.85);
      if (onceToday('tk.cimu')) apply({ mood: 10, stars: 2 });
      stamp('taroko');
      return `${t.looked}\n\n“${t.phrase}”`;
    }, t.phrase);
  },
  tk_rockSign() {
    const t = tk().rock;
    quiz(objName('tk_rockSign'), '⚠️', t.text, t.q, t.a, t.right, t.wrong, () => onceToday('tk.rock') && apply({ study: 4, stars: 1 }));
  },
  tk_swallows: swallows,
  tk_shrine() {
    const t = tk().shrine;
    landmark('tk_shrine', '⛩️', t.text, t.bow, () => {
      sfx('water');
      sayEnglish(t.phrase, 0.85);
      if (onceToday('tk.shrine')) apply({ mood: 12, stars: 3 });
      stamp('taroko');
      return `${t.bowed}\n\n${t.phrase}`;
    }, t.phrase);
  },
  tk_bell() {
    const t = tk().bell;
    landmark('tk_bell', '🔔', t.text, t.ring, () => {
      sfx('bell');
      burst('note', 6);
      sayEnglish(t.phrase, 0.85);
      if (onceToday('tk.bell')) apply({ mood: 10, stars: 2, study: 3 });
      return `${t.rang}\n\n“${t.phrase}”`;
    }, t.phrase);
  },
  tk_buluowan() {
    const t = tk().buluowan;
    landmark('tk_buluowan', '📣', t.text, t.shout, () => {
      sayEnglish(t.hello, 1);
      window.setTimeout(() => sayEnglish(t.hello, 0.8), 900);
      window.setTimeout(() => sayEnglish(t.hello, 0.7), 1800);
      burst('note', 4);
      if (onceToday('tk.buluowan')) apply({ mood: 10, stars: 2 });
      stamp('taroko');
      return t.echo;
    }, t.hello);
  },
  tk_weave() {
    const t = tk().weave;
    factQuiz('tk_weave', '🧵', t.text, t.try, t, () => apply({ study: 5, stars: 2 }));
  },
  tk_monkeys() {
    const t = tk().monkeys;
    quiz(objName('tk_monkeys'), '🐒', t.text, t.q, t.a, t.right, t.wrong, () => onceToday('tk.monkeys') && apply({ study: 4, stars: 2 }));
  },
  tk_mochi: () => order('tk_mochi', '🍡', tk().mochi, { energy: 14, mood: 10 }),
  tk_shakadang() {
    const t = tk().shakadang;
    factQuiz('tk_shakadang', '💧', t.text, t.look, t, () => {
      apply({ mood: 12, stars: 3 });
      stamp('taroko');
    });
  },
  tk_bikeRent: bikeRide,
  tk_skip: skipStone,
  tk_sunrise: sunrise,
  tk_stargaze: stargaze,
  tk_museum() {
    const t = tk().museum;
    factQuiz('tk_museum', '🐟', t.text, t.learn, t, () => {
      apply({ study: 6, stars: 2 });
      stamp('beach');
    });
  },
  tk_sausage: () => order('tk_sausage', '🌭', tk().sausage, { energy: 20, mood: 8 }),
});

/** NPC talk: the ranger asks a safety question, the rest share a line (some are English practice). */
registerTalk((id) => {
  const t = tk().talk;
  if (id === 'ranger') {
    const r = t.ranger;
    quiz(npcName('ranger'), '⛑️', r.intro, r.q, r.a, r.right, r.wrong, () => onceToday('tk.rangerQuiz') && apply({ study: 4, stars: 1 }));
    return true;
  }
  if (id === 'tk_mochiAuntie') {
    say({
      who: npcName(id),
      icon: '🍡',
      text: pick(t.tk_mochiAuntie),
      choices: [{ text: `🍡 ${objName('tk_mochi')}`, run: () => order('tk_mochi', '🍡', tk().mochi, { energy: 14, mood: 10 }) }, { text: m().common.back }],
    });
    return true;
  }
  const lines = (t as unknown as Record<string, readonly string[] | undefined>)[id];
  if (!id.startsWith('tk_') || !Array.isArray(lines)) return false;
  const icons: Record<string, string> = {
    tk_guide: '🚩', tk_rangerB: '⛑️', tk_weaver: '🧵', tk_fisher: '🎣', tk_kid1: '🥏', tk_kid2: '🪨',
    tk_cyclist: '🚲', tk_photographer: '📷', tk_bonito: '🐟', tk_stargazer: '🔭',
  };
  const line = pick(lines).replace(/^English practice:\s*/, '').replace(/\s*\(English practice\)$/, '');
  // Speak the quoted English phrases (in every language the quotes stay in English).
  const quoted = [...line.matchAll(/“([^”]+)”/g)].map((q) => q[1]).filter((q) => /^[\x20-\x7e’]+$/.test(q));
  const en = quoted.length ? quoted.join(' … ') : undefined;
  if (en) sayEnglish(en, 0.9);
  say({ who: npcName(id), icon: icons[id] ?? '💬', text: line, en });
  return true;
});
